/** Return review, lifecycle actions, exchange stats, and manual creation. */
import { endpoints } from '@/config/env';
import type { ReturnsQuery } from '@/config/env';
import type {
  ExchangeStats,
  ManualReturnRequest,
  Page,
  PickupAddress,
  QcDisposition,
  QcOutcome,
  RefundMode,
  ReturnDetailResponse,
  ReturnRecord,
  ReviewAction,
  ShopifyOrder,
} from '@/types';
import { request } from './client';

export function listReturns(
  query: ReturnsQuery,
  signal?: AbortSignal,
): Promise<Page<ReturnRecord> | null> {
  return request<Page<ReturnRecord> | null>(endpoints.returns(query), { signal });
}

export function fetchReturnDetail(
  returnId: string,
  signal?: AbortSignal,
): Promise<ReturnDetailResponse | null> {
  return request<ReturnDetailResponse | null>(endpoints.returnDetail(returnId), { signal });
}

export function reviewReturn(
  returnId: string,
  action: ReviewAction,
  internalNotes?: string,
): Promise<{ returnId: string; status: string } | null> {
  return request(endpoints.returnReview(returnId), {
    method: 'PUT',
    body: { action, internalNotes },
  });
}

export function recordQc(
  returnId: string,
  qcOutcome: QcOutcome,
  disposition: QcDisposition,
  internalNotes?: string,
): Promise<unknown> {
  return request(endpoints.returnQc(returnId), {
    method: 'PUT',
    body: { qcOutcome, disposition, internalNotes },
  });
}

export function schedulePickup(returnId: string): Promise<unknown> {
  return request(endpoints.returnSchedulePickup(returnId), { method: 'PUT' });
}

export function triggerRefund(returnId: string): Promise<unknown> {
  return request(endpoints.returnRefund(returnId), { method: 'PUT' });
}

export function partialRefund(
  returnId: string,
  amount: number,
  reason: string,
  refundMode?: RefundMode,
): Promise<unknown> {
  return request(endpoints.returnPartialRefund(returnId), {
    method: 'PUT',
    body: { amount, reason, refundMode },
  });
}

export function shipBack(returnId: string, note?: string): Promise<unknown> {
  return request(endpoints.returnShipBack(returnId), { method: 'PUT', body: { note } });
}

export function updatePickupAddress(returnId: string, address: PickupAddress): Promise<unknown> {
  return request(endpoints.returnPickupAddress(returnId), { method: 'PUT', body: address });
}

export function updateInternalNotes(returnId: string, internalNotes: string): Promise<unknown> {
  return request(endpoints.returnReview(returnId), { method: 'PUT', body: { internalNotes } });
}

export function fetchExchangeStats(
  range: { from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<ExchangeStats | null> {
  return request<ExchangeStats | null>(endpoints.exchangeStats(range), { signal });
}

/**
 * Order lookup for the manual-return wizard. Provide at least one identifier.
 * The endpoint returns a list of matching orders (possibly empty).
 */
export function lookupOrder(
  orderNumber?: string,
  email?: string,
  phone?: string,
  signal?: AbortSignal,
): Promise<ShopifyOrder[] | null> {
  return request<ShopifyOrder[] | null>(endpoints.orderLookup(orderNumber, email, phone), { signal });
}

export function createManualReturn(payload: ManualReturnRequest): Promise<ReturnRecord | null> {
  return request<ReturnRecord | null>(endpoints.manualReturn(), { method: 'POST', body: payload });
}
